import { Swiper, SwiperSlide } from 'swiper/react';

import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Image from 'components/global/Image';
import SwiperFooter from 'components/global/SwiperFooter';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import * as S from './ModuleDetail.styled';

const ModuleFeature = () => {
	const { selectedModule } = useSelector((state) => state.otoPackageList);
	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const [swiperRef, setSwiperRef] = useState(null);

	const features = useMemo(() => {
		if (!selectedModule) {
			return [];
		}
		const features = [];
		for (let stepNumber = 1; stepNumber < 20; stepNumber += 1) {
			const titleKey = `${selectedModule?.slug}.feature${stepNumber}`;
			const descriptionKey = `${selectedModule?.slug}.feature${stepNumber}.description`;
			const imageKey = `${selectedModule?.slug}.feature${stepNumber}.image`;
			if (t(titleKey) !== titleKey && t(descriptionKey) !== descriptionKey) {
				features.push({
					title: titleKey,
					description: descriptionKey,
					image: imageKey
				});
			} else {
				break;
			}
		}
		if (isMobile) {
			return features;
		}
		const pages = [];
		for (let i = 0; i < features.length; i += 5) {
			pages.push(features.slice(i, i + 5));
		}
		return pages;
	}, [selectedModule, isMobile]);

	if (features.length === 0) {
		return null;
	}

	return (
		<S.Features>
			<Typography variant="h2" fontWeight="800" noWrap align="center">
				<Trans i18nKey={`${selectedModule?.slug}.features.title`} />
			</Typography>

			<Swiper spaceBetween={24} slidesPerView={1} autoHeight={true} onSwiper={setSwiperRef}>
				{features.map((page, index) =>
					isMobile ? (
						<SwiperSlide key={page.title}>
							<S.Feature key={page.title}>
								<Image
									url={t(page.image)}
									defaultUrl={t(page.image)}
									alt={page.title}
									width="9rem"
									height="9rem"
								/>
								<S.FeatureContent>
									<Typography variant="p1" fontWeight="700" noWrap>
										<Trans i18nKey={page.title} />
									</Typography>
									<Typography variant="p2" color="var(--content-2)" noWrap>
										<Trans i18nKey={page.description} />
									</Typography>
								</S.FeatureContent>
							</S.Feature>
						</SwiperSlide>
					) : (
						<SwiperSlide key={index}>
							<S.FeatureRow className="feature-top-row" size={page.length}>
								{page.slice(0, 3).map((item) => (
									<S.Feature key={item.title}>
										<Image
											url={t(item.image)}
											defaultUrl={t(item.image)}
											alt={item.title}
											width="9rem"
											height="9rem"
										/>
										<S.FeatureContent>
											<Typography variant="p1" fontWeight="700" noWrap>
												<Trans i18nKey={item.title} />
											</Typography>
											<Typography variant="p2" color="var(--content-2)" noWrap>
												<Trans i18nKey={item.description} />
											</Typography>
										</S.FeatureContent>
									</S.Feature>
								))}
							</S.FeatureRow>
							{page.length > 3 && (
								<S.FeatureRow className="feature-bottom-row">
									{page.slice(3, 5).map((item) => (
										<S.Feature key={item.title}>
											<Image
												url={t(item.image)}
												defaultUrl={t(item.image)}
												alt={item.title}
												width="9rem"
												height="9rem"
											/>
											<S.FeatureContent>
												<Typography variant="p1" fontWeight="700" noWrap>
													<Trans i18nKey={item.title} />
												</Typography>
												<Typography variant="p2" color="var(--content-2)" noWrap>
													<Trans i18nKey={item.description} />
												</Typography>
											</S.FeatureContent>
										</S.Feature>
									))}
								</S.FeatureRow>
							)}
						</SwiperSlide>
					)
				)}
				{features.length > 1 && swiperRef && <SwiperFooter swiperRef={swiperRef} />}
			</Swiper>
		</S.Features>
	);
};

export default ModuleFeature;
